// import indexOf from 'lodash/indexOf'
import get from 'lodash/get'
import forEach from 'lodash/forEach'
import isNumber from 'lodash/isNumber'
import values from 'lodash/values'
import {
  Map, List, Set, fromJS,
} from 'immutable'
import * as actions from './actions'
import { CATEGORIES } from './notificationCategory'

export const initialState = Map()

/**
 * Where one tab's page of notifications lives.
 *
 * ⚠️ `['user', <category>, …]` is kept SEPARATE from `['recent', …]`, which is the bell's own
 * unfiltered list. Collapsing the tabs onto `recent` would make the bell's unread badge report
 * "new in the tab you last opened" instead of "new" (CO-768).
 */
const userPath = (category) => ['user', category]

/**
 * Where one bell-dropdown TAB's preview rows live.
 *
 * ⚠️ A third place, and all three are deliberate. `['recent', …]` is the unfiltered list the
 * unread dot is counted from; `['user', <category>]` is the paginated `/notifications` page;
 * this is the dropdown's 10-row preview per tab. Collapsing the dropdown onto `recent` would
 * mean partitioning ten mixed rows in memory — the exact bug CO-768 fixed on the page, where a
 * member with ten fresh Content rows saw an empty Community tab. Collapsing it onto `user`
 * would make the dropdown and the page share a page cursor (CO-768).
 */
const menuPath = (category) => ['menu', category]

export default function reducer (state = initialState, action) {
  //
  const {
    type: actionType,
    payload,
  } = action

  switch (actionType) {
    //
    case actions.NOTIFICATIONS_CLEAR_ALL:
      return state.delete('user')

    case actions.NOTIFICATIONS_GET_ALL: {
      const category = get(payload, 'category', CATEGORIES.CONTENT)
      return state.setIn([...userPath(category), 'processing'], true)
    }

    case actions.NOTIFICATIONS_GET_RECENT:
      return state.setIn(['recent', 'processing'], true)

    case actions.NOTIFICATIONS_GET_MENU: {
      const category = get(payload, 'category', CATEGORIES.CONTENT)
      return state.setIn([...menuPath(category), 'processing'], true)
    }

    /**
     * One bell-dropdown tab's preview rows.
     *
     * No `append` and no `p`: the dropdown is a fixed-size preview with "View All" beneath it,
     * so there is nothing to paginate. A failed fetch still writes `rows`, for the same reason
     * the page does — an absent `rows` is how the tab tells "not fetched yet" from "fetched and
     * empty", and without it the tab spins forever.
     */
    case actions.NOTIFICATIONS_SET_MENU: {
      const category = get(payload, 'category', CATEGORIES.CONTENT)
      const failed = get(payload, 'failed', false)
      return state.updateIn(menuPath(category), Map(), (slice) => {
        if (failed) {
          return slice.set('processing', false).set('rows', slice.get('rows', List()))
        }
        return slice.set('processing', false).set('rows', fromJS(get(payload, 'rows', [])))
      })
    }

    /**
     * One page of ONE tab's notifications.
     *
     * `append` is what the "Load more" button buys: a page fetched with an `after` cursor is
     * concatenated onto the rows the tab already holds rather than replacing them. Deliberately
     * not infinite scroll — a scroll-driven fetch fires far more often, and each one is a page
     * the member did not ask for.
     */
    case actions.NOTIFICATIONS_SET_ALL: {
      const category = get(payload, 'category', CATEGORIES.CONTENT)
      const failed = get(payload, 'failed', false)
      const append = get(payload, 'append', false)
      const total = get(payload, 'total')
      return state.updateIn(userPath(category), Map(), (slice) => {
        const existing = slice.get('rows', List())
        // A failed request keeps whatever the tab already had, and keeps its cursor where it
        // was, so "Load more" is still offered rather than silently ending the list on a network
        // blip. `rows` is still written on a first-page failure: an absent `rows` is how the
        // page tells "not fetched yet" from "fetched and empty", and without it the tab sits on
        // a spinner for good.
        if (failed) {
          return slice.set('processing', false).set('rows', existing)
        }
        const fetched = fromJS(get(payload, 'rows', []))
        return slice
          .set('processing', false)
          .set('rows', append ? existing.concat(fetched) : fetched)
          .set('total', isNumber(total) ? total : fetched.size)
          // ⚠️ Cursor paging. `endCursor` is what the next "Load more" sends as `after`, and
          // `hasNextPage` is the only thing that ends the list. There is deliberately no page
          // index: an offset over a list that grows at the top repeats or skips rows.
          .set('hasNextPage', get(payload, 'hasNextPage', false))
          .set('endCursor', get(payload, 'endCursor', null))
      })
    }

    case actions.NOTIFICATIONS_GET_SUBSCRIPTIONS: {
      return state.setIn(['subscriptions', 'processing'], true)
    }

    case actions.NOTIFICATIONS_CLEAR_SUBSCRIBABLE: {
      const { contentId, type } = payload
      return state.deleteIn(['subscribables', type, contentId])
    }

    case actions.NOTIFICATIONS_SET_SUBSCRIPTIONS: {
      const { data, total } = payload
      return state.updateIn(['subscriptions'], Map(), (recent) => (
        recent.set('processing', false).set('data', fromJS(data)).set('total', total)
      ))
    }

    case actions.NOTIFICATIONS_SET_RECENT:
      return state.updateIn(['recent'], Map(), (recent) => (
        recent.set('processing', false).set('data', fromJS(payload))
      ))

    // A steady-state poll returned the same notification ids, so the watcher
    // skipped both the content fan-out and the data write. Clear only the
    // transient `processing` flag GET_RECENT set — leaving `data` untouched
    // preserves its reference, so the bell doesn't re-render its list every poll.
    case actions.NOTIFICATIONS_SET_RECENT_UNCHANGED:
      return state.setIn(['recent', 'processing'], false)

    /**
     * Mark the notifications in `payload.ids` as read, everywhere they are held.
     *
     * ⚠️ This used to ignore its payload entirely and blanket-set `received: true` on every row
     * in `['recent','data']`. That made per-tab reading impossible — reading one tab cleared the
     * other — and it also meant a video-promotion impression, which reports a single id, marked
     * the member's whole bell as read (CO-768).
     *
     * Both the bell's list and every loaded tab are updated, keyed ON ID. That is why no
     * category travels in the payload and none is needed: an id belongs to exactly one
     * category's rows, so matching by id cannot touch the other tab. A `category` here is
     * precisely where the tab filter would leak into the bell's unfiltered badge.
     *
     * An empty `ids` is a no-op rather than "all of them" — the inverse of the old reading, and
     * the safe direction: marking nothing is recoverable on the next impression, marking
     * everything is not.
     */
    case actions.NOTIFICATIONS_SET_RECEIVED: {
      // ⚠️ Normalised to strings on BOTH sides. The feed's `id` is `ID`, so it arrives a string
      // where REST sent a number; an un-normalised `Set.has` would match nothing and the badge
      // would never clear, with nothing throwing to say so.
      const ids = Set(get(payload, 'ids', []).map((id) => String(id)))
      if (ids.isEmpty()) {
        return state
      }
      const markRows = (rows) => rows.map((r) => (
        ids.has(String(r.get('id'))) ? r.set('received', true) : r
      ))
      let next = state.hasIn(['recent', 'data'])
        ? state.updateIn(['recent', 'data'], markRows)
        : state
      // Every slice that can hold the row, keyed on id: the bell's unfiltered list, the page's
      // two tabs, and the dropdown's two preview tabs. A row can sit in three of these at once
      // (bell list + page tab + dropdown tab), and leaving any one stale shows the same
      // notification as unread on one surface and read on another.
      forEach(values(CATEGORIES), (category) => {
        forEach([userPath(category), menuPath(category)], (base) => {
          const rowsPath = [...base, 'rows']
          if (next.hasIn(rowsPath)) {
            next = next.updateIn(rowsPath, markRows)
          }
        })
      })
      return next
    }

    case actions.NOTIFICATIONS_SET_SUBSCRIBER: {
      const { type, contentId, subscriber } = payload
      const key = ['subscribables', type, contentId]
      return state.updateIn(key, Map(), (s) => (
        s.set('subscriber', subscriber)
      ))
    }

    case actions.NOTIFICATIONS_SET_SUBSCRIBABLE: {
      const { entity, contentId, type } = payload
      const subscription = fromJS(entity || {})
      const path = ['subscribables', type, contentId]
      const isSubscribable = isNumber(subscription.get('id'))
      const updatedSub = subscription.set('type', type).set('isSubscribable', isSubscribable)
      return state.setIn(path, updatedSub)
    }

    default:
      return state
  }
}
